import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { RiArrowRightLine, RiArrowLeftLine } from 'react-icons/ri';
import { usePublicSettings } from '../../../context/PublicSettingsContext.js';
import { useStorePath } from '../../../lib/storefront/storePath.js';
import { fetchGalleryPhotos } from '../../../api/galleryApi';
import { getGalleryPhotosPlaceholder } from '../../../data/galleryPhotosPlaceholder.js';
import { resolveMediaUrl } from '../../../utils/mediaUrl.js';
import './Gallery.css';

const buildCategories = (photos, t) => {
    const cats = [...new Set(photos.map((p) => p.category).filter(Boolean))];
    return [
        { key: 'all', label: t('gallery.all') },
        ...cats.map((c) => ({
            key: c,
            label: t(`gallery.categories.${c}`, { defaultValue: c }),
        })),
    ];
};

const PEEK_COUNT = 3;

const Gallery = () => {
    const { t, i18n } = useTranslation();
    const { publicSettings } = usePublicSettings();
    const storePath = useStorePath();
    const galleryPageEntry = publicSettings?.storefrontPages?.gallery;
    const showGalleryLink = !galleryPageEntry || typeof galleryPageEntry !== 'object' || galleryPageEntry.enabled !== false;
    const isRtl = i18n.dir(i18n.language) === 'rtl';

    const [photos, setPhotos] = useState([]);
    const [usingPlaceholder, setUsingPlaceholder] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(false);
    const [activeCategory, setActiveCategory] = useState('all');
    const [activeIndex, setActiveIndex] = useState(0);
    const [flipping, setFlipping] = useState(false);
    const [flipDir, setFlipDir] = useState(1);
    const [entered, setEntered] = useState(false);
    const [fullscreen, setFullscreen] = useState(false);
    const [dragStart, setDragStart] = useState(null);
    const [dragDelta, setDragDelta] = useState(0);
    const flipTimeout = useRef(null);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                setLoading(true);
                setError(false);
                const data = await fetchGalleryPhotos();
                if (!cancelled) {
                    const fromApi = Array.isArray(data)
                        ? data.filter((p) => resolveMediaUrl(p.imgUrl))
                        : [];
                    if (fromApi.length > 0) {
                        setPhotos(fromApi);
                        setUsingPlaceholder(false);
                    } else {
                        setPhotos(getGalleryPhotosPlaceholder(t));
                        setUsingPlaceholder(true);
                    }
                    setTimeout(() => setEntered(true), 100);
                }
            } catch {
                if (!cancelled) {
                    setPhotos(getGalleryPhotosPlaceholder(t));
                    setUsingPlaceholder(true);
                    setTimeout(() => setEntered(true), 100);
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [t]);

    useEffect(() => {
        if (!usingPlaceholder) return;
        setPhotos(getGalleryPhotosPlaceholder(t));
    }, [i18n.language, usingPlaceholder, t]);

    const filtered = activeCategory === 'all'
        ? photos
        : photos.filter((p) => p.category === activeCategory);

    const categories = buildCategories(photos, t);
    const current = filtered[activeIndex] || null;
    const total = filtered.length;

    const navigate = useCallback((dir) => {
        if (flipping || total <= 1) return;
        setFlipDir(dir);
        setFlipping(true);
        clearTimeout(flipTimeout.current);
        flipTimeout.current = setTimeout(() => {
            setActiveIndex((i) => (i + dir + total) % total);
            setFlipping(false);
        }, 480);
    }, [flipping, total]);

    const changeCategory = (key) => {
        setActiveCategory(key);
        setActiveIndex(0);
        setEntered(false);
        setTimeout(() => setEntered(true), 80);
    };

    const onPointerDown = (e) => { setDragStart(e.clientX); setDragDelta(0); };
    const onPointerMove = (e) => { if (dragStart !== null) setDragDelta(e.clientX - dragStart); };
    const onPointerUp = () => {
        if (Math.abs(dragDelta) > 60) navigate(dragDelta < 0 ? 1 : -1);
        setDragStart(null);
        setDragDelta(0);
    };

    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'ArrowRight') navigate(1);
            if (e.key === 'ArrowLeft') navigate(-1);
            if (e.key === 'Escape') setFullscreen(false);
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [navigate]);

    useEffect(() => {
        document.body.style.overflow = fullscreen ? 'hidden' : '';
        return () => { document.body.style.overflow = ''; };
    }, [fullscreen]);

    const peekCards = Array.from({ length: Math.min(PEEK_COUNT, total - 1) }, (_, i) => ({
        photo: filtered[(activeIndex + i + 1) % total],
        depth: i + 1,
    }));

    const dragOffset = dragStart !== null ? Math.max(-120, Math.min(120, dragDelta)) : 0;
    const dragRotate = dragOffset * 0.08;

    return (
        <>
            <section
                className={`gallery-root${entered ? ' gallery-root--entered' : ''}`}
                id="gallery"
                aria-label={t('gallery.title')}
            >
                <div className="gallery-grain" aria-hidden="true" />

                <div className="container gallery-inner">
                    <header className="gallery-header">
                        <div className="gallery-title-row">
                            <h2 className="gallery-title">{t('gallery.title')}</h2>
                            {total > 0 && (
                                <span className="gallery-count-badge">
                                    <span className="gallery-count-current">{String(activeIndex + 1).padStart(2, '0')}</span>
                                    <span className="gallery-count-sep">/</span>
                                    <span className="gallery-count-total">{String(total).padStart(2, '0')}</span>
                                </span>
                            )}
                        </div>
                        {t('gallery.description') && (
                            <p className="gallery-desc">{t('gallery.description')}</p>
                        )}
                        {showGalleryLink && (
                            <Link to={storePath(`/${i18n.language}/gallery`)} className="gallery-view-all">
                                {t('gallery.viewAll')}
                                {isRtl ? <RiArrowLeftLine aria-hidden="true" /> : <RiArrowRightLine aria-hidden="true" />}
                            </Link>
                        )}
                    </header>

                    {categories.length > 1 && (
                        <nav className="gallery-tabs" aria-label={t('gallery.categoriesNavAria')}>
                            {categories.map((c) => (
                                <button
                                    key={c.key}
                                    type="button"
                                    className={`gallery-tab${activeCategory === c.key ? ' gallery-tab--active' : ''}`}
                                    onClick={() => changeCategory(c.key)}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </nav>
                    )}

                    <div className="gallery-stage">
                        {loading && (
                            <div className="gallery-loading">
                                <div className="gallery-loader"><span /><span /><span /><span /></div>
                            </div>
                        )}
                        {!loading && error && <p className="gallery-error">{t('gallery.error')}</p>}
                        {!loading && !error && total === 0 && (
                            <p className="gallery-empty">{t('gallery.empty')}</p>
                        )}

                        {!loading && !error && current && (
                            <>
                                {[...peekCards].reverse().map(({ photo, depth }) => (
                                    <div
                                        key={`peek-${depth}`}
                                        className="gallery-card gallery-card--peek"
                                        style={{ '--depth': depth, zIndex: 10 - depth }}
                                        aria-hidden="true"
                                    >
                                        <img src={resolveMediaUrl(photo.imgUrl)} alt="" draggable={false} />
                                        <div className="gallery-card-veil" />
                                    </div>
                                ))}

                                <div
                                    className={`gallery-card gallery-card--active${flipping ? (flipDir > 0 ? ' gallery-card--flip-next' : ' gallery-card--flip-prev') : ''}`}
                                    style={{
                                        zIndex: 20,
                                        transform: dragStart !== null
                                            ? `translateX(${dragOffset}px) rotate(${dragRotate}deg)`
                                            : undefined,
                                        transition: dragStart !== null ? 'none' : undefined,
                                    }}
                                    onPointerDown={onPointerDown}
                                    onPointerMove={onPointerMove}
                                    onPointerUp={onPointerUp}
                                    onPointerLeave={onPointerUp}
                                    onClick={() => Math.abs(dragDelta) < 5 && setFullscreen(true)}
                                    role="img"
                                    aria-label={`${current.description || t('gallery.imageFallback')}. ${t('gallery.openLightbox')}`}
                                >
                                    <img
                                        src={resolveMediaUrl(current.imgUrl)}
                                        alt={current.description || t('gallery.imageFallback')}
                                        draggable={false}
                                    />
                                    <div className="gallery-card-info">
                                        {current.category && (
                                            <span className="gallery-card-tag">
                                                {t(`gallery.categories.${current.category}`, {
                                                    defaultValue: current.category,
                                                })}
                                            </span>
                                        )}
                                        {current.description && (
                                            <p className="gallery-card-caption">{current.description}</p>
                                        )}
                                        <span className="gallery-card-zoom" aria-hidden="true">
                                            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                                                <path d="M13 3h4v4M7 17H3v-4M17 3l-6.5 6.5M3 17l6.5-6.5" />
                                            </svg>
                                        </span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>

                    {!loading && !error && total > 1 && (
                        <div className="gallery-controls">
                            <button className="gallery-nav-btn" onClick={() => navigate(-1)} type="button" aria-label={t('gallery.prev')}>
                                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                                    <path d="M12.5 5L7.5 10l5 5" />
                                </svg>
                                <span>{t('gallery.prev')}</span>
                            </button>

                            <div className="gallery-pips" aria-hidden="true">
                                {total <= 9
                                    ? Array.from({ length: total }, (_, i) => (
                                        <button
                                            key={i}
                                            type="button"
                                            className={`gallery-pip${i === activeIndex ? ' gallery-pip--active' : ''}`}
                                            onClick={() => {
                                                if (!flipping) {
                                                    const dir = i > activeIndex ? 1 : -1;
                                                    setFlipDir(dir);
                                                    setFlipping(true);
                                                    clearTimeout(flipTimeout.current);
                                                    flipTimeout.current = setTimeout(() => {
                                                        setActiveIndex(i);
                                                        setFlipping(false);
                                                    }, 480);
                                                }
                                            }}
                                            aria-label={t('gallery.photoIndex', { n: i + 1 })}
                                        />
                                    ))
                                    : (
                                        <div className="gallery-progress-track">
                                            <div
                                                className="gallery-progress-fill"
                                                style={{ width: `${((activeIndex + 1) / total) * 100}%` }}
                                            />
                                        </div>
                                    )}
                            </div>

                            <button className="gallery-nav-btn" onClick={() => navigate(1)} type="button" aria-label={t('gallery.next')}>
                                <span>{t('gallery.next')}</span>
                                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                                    <path d="M7.5 5l5 5-5 5" />
                                </svg>
                            </button>
                        </div>
                    )}

                    {!loading && !error && total > 1 && (
                        <p className="gallery-hint" aria-hidden="true">{t('gallery.dragHint')}</p>
                    )}
                </div>
            </section>

            {fullscreen && current && (
                <div
                    className="gallery-fullscreen"
                    onClick={() => setFullscreen(false)}
                    role="dialog"
                    aria-modal="true"
                    aria-label={t('gallery.lightboxTitle')}
                >
                    <button className="gallery-fs-close" onClick={() => setFullscreen(false)} type="button" aria-label={t('gallery.closeLightbox')}>
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
                            <path d="M5 5l10 10M15 5L5 15" />
                        </svg>
                    </button>
                    <button className="gallery-fs-nav gallery-fs-nav--prev" onClick={(e) => { e.stopPropagation(); navigate(-1); }} type="button" aria-label={t('gallery.prev')}>
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M12.5 5L7.5 10l5 5" /></svg>
                    </button>
                    <img
                        className="gallery-fs-img"
                        src={resolveMediaUrl(current.imgUrl)}
                        alt={current.description || t('gallery.imageFallback')}
                        onClick={(e) => e.stopPropagation()}
                        draggable={false}
                    />
                    <button className="gallery-fs-nav gallery-fs-nav--next" onClick={(e) => { e.stopPropagation(); navigate(1); }} type="button" aria-label={t('gallery.next')}>
                        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true"><path d="M7.5 5l5 5-5 5" /></svg>
                    </button>
                    {current.description && (
                        <p className="gallery-fs-caption" onClick={(e) => e.stopPropagation()}>{current.description}</p>
                    )}
                </div>
            )}
        </>
    );
};

export default Gallery;